import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Search, Plus, Package, CheckCircle, Info } from 'lucide-react';
import type { SnapshotMutation, PackContext, StoreEditRow } from '@/types/snapshot-edit-types';

/**
 * Add Store Modal (Pack Campaign) - Simplified
 * 
 * BEHAVIOR:
 * - Minimal modal: Store selection + Pack selection (only if multiple packs)
 * - No format, quantity, operator, or reference-store UI
 * - Configuration is applied internally by:
 *   1. Selecting deterministic reference store in chosen pack
 *   2. Cloning its full configuration
 *   3. Backend resolves operator overrides
 * - If pack has no stores, fall back to pack definition
 */

interface AddStoreModalProps {
    isOpen: boolean;
    onClose: () => void;
    campaignId: number;
    retailerId: number;
    existingStoreIds: Set<number>;
    packs: PackContext[];
    onConfirm: (mutation: SnapshotMutation) => void;
    // Existing stores in snapshot, organized by pack for reference store lookup
    existingStores?: StoreEditRow[];
}

export function AddStoreModal({
    isOpen,
    onClose,
    campaignId,
    retailerId,
    existingStoreIds,
    packs,
    onConfirm,
    existingStores = [],
}: AddStoreModalProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [allRetailerStores, setAllRetailerStores] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStore, setSelectedStore] = useState<any | null>(null);
    const [selectedPackId, setSelectedPackId] = useState<number | null>(null);
    const [packTemplates, setPackTemplates] = useState<Map<number, any[]>>(new Map());
    const [isLoadingPackTemplates, setIsLoadingPackTemplates] = useState(false);

    // Auto-select pack if only one exists
    useEffect(() => {
        if (packs.length === 1 && !selectedPackId) {
            setSelectedPackId(packs[0].packId);
        }
    }, [packs, selectedPackId]);

    // Fetch all stores for the retailer
    useEffect(() => {
        if (isOpen && retailerId) {
            const fetchStores = async () => {
                setIsLoading(true);
                try {
                    const response = await fetch(`/api/retailers/${retailerId}/stores?all=true`, {
                        credentials: 'include',
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setAllRetailerStores(data);
                    }
                } catch (error) {
                    console.error('Failed to fetch retailer stores:', error);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchStores();
        }
    }, [isOpen, retailerId]);

    // Fetch pack templates (fallback when no reference store exists)
    useEffect(() => {
        if (isOpen && packs.length > 0) {
            const fetchPackTemplates = async () => {
                setIsLoadingPackTemplates(true);
                const templateMap = new Map();
                try {
                    for (const pack of packs) {
                        const response = await fetch(`/api/packs/${pack.packId}/details`, {
                            credentials: 'include',
                        });
                        if (response.ok) {
                            const data = await response.json();
                            const templates = Array.isArray(data) ? data : (data.templates || []);
                            templateMap.set(pack.packId, templates);
                        }
                    }
                    setPackTemplates(templateMap);
                } catch (error) {
                    console.error('Failed to fetch pack templates:', error);
                } finally {
                    setIsLoadingPackTemplates(false);
                }
            };
            fetchPackTemplates();
        }
    }, [isOpen, packs]);

    // Filter stores to exclude already-assigned stores
    const filteredStores = useMemo(() => {
        return allRetailerStores.filter(s =>
            !existingStoreIds.has(s.id) &&
            (s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                s.retailer_identifier?.toLowerCase().includes(searchQuery.toLowerCase()))
        ).slice(0, 50);
    }, [allRetailerStores, existingStoreIds, searchQuery]);

    // Get stores in the selected pack (for reference store selection)
    const storesInSelectedPack = useMemo(() => {
        if (!selectedPackId) return [];
        return existingStores.filter(s =>
            s.assignments.some(a => a.packId === selectedPackId)
        );
    }, [selectedPackId, existingStores]);

    // Find deterministic reference store (first store in pack alphabetically)
    const referenceStore = useMemo(() => {
        if (storesInSelectedPack.length === 0) return null;
        // Sort by store name for deterministic selection
        const sorted = [...storesInSelectedPack].sort((a, b) =>
            a.storeName.localeCompare(b.storeName)
        );
        return sorted[0];
    }, [storesInSelectedPack]);

    const handleConfirm = () => {
        if (!selectedStore || !selectedPackId) return;

        let formats: any[];

        if (referenceStore) {
            // Clone from reference store (filtered to selected pack)
            formats = referenceStore.assignments
                .filter(a => a.packId === selectedPackId)
                .map(a => ({
                    packId: a.packId,
                    mediaFormatId: a.formatId,
                    mediaFormatName: a.formatName,
                    operatorId: 0, // Do not clone operator from reference store; let server resolve or default to Unassigned
                    operatorName: 'Unassigned',
                    quantity: a.quantity
                }));
        } else {
            // Fallback: Use pack templates (no existing stores in pack)
            const templates = packTemplates.get(selectedPackId) || [];
            formats = templates.map((t: any) => ({
                packId: selectedPackId,
                mediaFormatId: t.mediaFormatId,
                mediaFormatName: t.mediaFormatName,
                operatorId: 0, // Backend will resolve
                operatorName: 'Unassigned',
                quantity: t.quantity || 1
            }));
        }

        if (formats.length === 0) {
            console.error('[AddStoreModal] No formats to clone - check pack configuration');
            return;
        }

        const mutation: SnapshotMutation = {
            type: 'ADD_STORE',
            storeId: selectedStore.id,
            storeName: selectedStore.name,
            storeCode: selectedStore.retailer_identifier,
            storeAddress: selectedStore.address,
            town: selectedStore.region,
            formats
        };

        onConfirm(mutation);
        reset();
        onClose();
    };

    const reset = () => {
        setSelectedStore(null);
        setSelectedPackId(packs.length === 1 ? packs[0].packId : null);
        setSearchQuery('');
    };

    const isValid = selectedStore && selectedPackId;
    const selectedPack = packs.find(p => p.packId === selectedPackId);

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col pt-6">
                <DialogHeader className="px-1">
                    <DialogTitle>Add Store</DialogTitle>
                    <DialogDescription>
                        Select a store to add to the campaign. Configuration will be cloned from existing stores.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-hidden grid grid-cols-2 gap-6 mt-4">
                    {/* Left: Store Selection */}
                    <div className="flex flex-col gap-4 overflow-hidden border-r pr-4">
                        <div className="space-y-2">
                            <Label>1. Select Store</Label>
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search stores..."
                                    className="pl-8"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        <ScrollArea className="flex-1 border rounded-md p-2">
                            {isLoading ? (
                                <div className="flex justify-center p-4"><Loader2 className="animate-spin" /></div>
                            ) : (
                                <div className="space-y-1">
                                    {filteredStores.map(store => (
                                        <div
                                            key={store.id}
                                            className={`p-2 rounded cursor-pointer text-sm hover:bg-accent transition-colors ${selectedStore?.id === store.id ? 'bg-primary/10 border-primary border' : 'border border-transparent'}`}
                                            onClick={() => setSelectedStore(store)}
                                        >
                                            <div className="font-medium">{store.name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {store.retailer_identifier && `Code: ${store.retailer_identifier} • `}
                                                {store.address}
                                            </div>
                                        </div>
                                    ))}
                                    {filteredStores.length === 0 && !isLoading && (
                                        <div className="text-sm text-center py-4 text-muted-foreground">
                                            No available stores found
                                        </div>
                                    )}
                                </div>
                            )}
                        </ScrollArea>
                    </div>

                    {/* Right: Pack Selection */}
                    <div className="flex flex-col gap-4 overflow-hidden">
                        <div className="space-y-2">
                            <Label>2. Select Pack</Label>
                            {packs.length === 1 ? (
                                <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded flex items-center gap-2">
                                    <Info className="h-3 w-3" />
                                    Single pack campaign - auto-selected
                                </div>
                            ) : (
                                <div className="text-xs text-muted-foreground">
                                    Choose which pack configuration to apply
                                </div>
                            )}
                        </div>

                        <ScrollArea className="flex-1">
                            <div className="space-y-2 pr-4">
                                {packs.map(pack => {
                                    const storeCount = existingStores.filter(s =>
                                        s.assignments.some(a => a.packId === pack.packId)
                                    ).length;
                                    const templates = packTemplates.get(pack.packId) || [];
                                    const isSelected = selectedPackId === pack.packId;

                                    return (
                                        <div
                                            key={pack.packId}
                                            className={`p-3 rounded cursor-pointer transition-colors border ${isSelected
                                                ? 'bg-primary/10 border-primary'
                                                : 'hover:bg-accent border-transparent'
                                                }`}
                                            onClick={() => setSelectedPackId(pack.packId)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Package className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium">{pack.packName}</span>
                                                </div>
                                                {isSelected && (
                                                    <CheckCircle className="h-4 w-4 text-primary" />
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1 flex gap-2">
                                                <Badge variant="secondary" className="text-[10px]">
                                                    {templates.length} formats
                                                </Badge>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </ScrollArea>
                    </div>
                </div>

                <DialogFooter className="mt-4 flex-col sm:flex-row gap-4">
                    {/* Summary */}
                    {selectedStore && selectedPack && (
                        <div className="flex-1 text-xs bg-muted/50 p-2 rounded-md flex items-center gap-4">
                            <span className="font-medium text-primary flex items-center gap-1">
                                <Plus className="h-3 w-3" /> Add {selectedStore.name}
                            </span>
                            <span className="text-muted-foreground">
                                to {selectedPack.packName}
                            </span>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>Cancel</Button>
                        <Button onClick={handleConfirm} disabled={!isValid}>
                            Add Store
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
